import { inject } from '@angular/core';
import { MessagingSubscriptionActions } from '@elio/emanager-front/push-messaging/action';
import { CACHE_DURATION } from '@elio/emanager-front/shared/app-config';
import { ElioError } from '@elio/shared/models';
import { catchElioError } from '@elio/shared/rxjs-utils';
import { isPast, newDateInXMinutes } from '@elio/shared/utils';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { TypedAction } from '@ngrx/store/src/models';
import { Observable, map, of, tap } from 'rxjs';
import { CacheManagerActions } from './cache-manager.actions';

export abstract class CacheManager {
  private cacheExpirationTime: Date | undefined = undefined;
  private currentCacheEntityId: number | undefined = undefined; // current offer/entity in the cache
  protected cacheDuration = inject(CACHE_DURATION);

  public loadEntities<T>({
    cacheEntityId,
    entities,
    next,
    error,
    noOp,
  }: {
    cacheEntityId: number | undefined; // undefined if you want the cache to never be refreshed
    entities: Observable<T[]>;
    next: (entities: T[]) => TypedAction<string>;
    error: (error: ElioError) => TypedAction<string>;
    noOp: () => TypedAction<string>;
  }) {
    if (this.isOfferCacheExpired(cacheEntityId)) {
      return entities.pipe(
        map((entities: T[]) => {
          this.refreshOfferCacheExpiration(cacheEntityId);
          return next(entities);
        }),
        catchElioError((err) => of(error(err)))
      );
    } else {
      return of(noOp());
    }
  }

  public clearCache() {
    this.cacheExpirationTime = undefined;
    this.currentCacheEntityId = undefined;
  }

  private isOfferCacheExpired(cacheEntityId?: number): boolean {
    return (
      !!cacheEntityId &&
      (!this.cacheExpirationTime ||
        (!!this.cacheExpirationTime && isPast(this.cacheExpirationTime)) ||
        cacheEntityId !== this.currentCacheEntityId)
    );
  }

  private refreshOfferCacheExpiration(cacheEntityId?: number) {
    this.cacheExpirationTime = newDateInXMinutes(this.cacheDuration);
    this.currentCacheEntityId = cacheEntityId;
  }

  readonly clearCache$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(MessagingSubscriptionActions.successAndLogout, MessagingSubscriptionActions.failureAndLogout),
      tap(() => this.clearCache()),
      map(() => CacheManagerActions.cacheCleared())
    );
  });

  constructor(protected actions$: Actions) {}
}
